import { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { LabelMarkupEntry } from "@redotech/admin-sdk/rpc/schema/integrations/returnbear-integration";
import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { getKey } from "@redotech/react-util/key";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
  tryCoalesceStringToRedoShopifyCountryCode,
} from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import {
  DEFAULT_RETURNBEAR_MARKUP_USD,
  ReturnBearMarkupKey,
} from "@redotech/redo-model/integration/returnbear/returnbear-utils";
import { ExternalReturnHubType, Team } from "@redotech/redo-model/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, listInput } from "@redotech/ui/form";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

const getReturnBearHubs = async (
  team: Team,
  client: RedoAdminRpcClient,
): Promise<RedoShopifyCountryCode[]> => {
  try {
    const useLocationFlowResponse = await client.getTeamFeatureFlagArm({
      teamId: team._id,
      flagKey: FeatureFlagKey.USE_LOCATIONS_FLOW,
    });

    const useLocationFlow = useLocationFlowResponse.value;

    if (useLocationFlow) {
      const response = await client.getReturnBearCountries({
        teamId: team._id,
      });
      return response.countries;
    }
  } catch (error) {
    console.error("Failed to fetch ReturnBear countries:", error);
  }

  const hubs = team.settings.locations.flatMap((loc) => {
    const countryCode = tryCoalesceStringToRedoShopifyCountryCode(
      loc.address?.country ?? "",
    );
    if (
      loc.externalReturnHub?.provider === ExternalReturnHubType.RETURNBEAR &&
      countryCode
    ) {
      return [countryCode];
    }
    return [];
  });

  return hubs;
};

const generateRouteMarkups = (
  eligibleCountries: RedoShopifyCountryCode[],
  destinations: RedoShopifyCountryCode[],
  existingMarkups: LabelMarkupEntry[],
): LabelMarkupEntry[] => {
  const routes: LabelMarkupEntry[] = [];

  for (const origin of eligibleCountries) {
    for (const destination of destinations) {
      const existingMarkup = existingMarkups.find(
        (m) => m.origin === origin && m.destination === destination,
      );

      // If a markup was explicitly saved (even if 0), use it; otherwise show the default
      const markup =
        existingMarkup !== undefined
          ? existingMarkup.markup
          : DEFAULT_RETURNBEAR_MARKUP_USD;

      routes.push({ origin, destination, markup });
    }
  }

  return routes;
};

const groupMarkupsByOrigin = (
  markups: LabelMarkupEntry[],
): Record<string, LabelMarkupEntry[]> => {
  return markups.reduce(
    (acc, markup) => {
      if (!acc[markup.origin]) {
        acc[markup.origin] = [];
      }
      acc[markup.origin].push(markup);
      return acc;
    },
    {} as Record<string, LabelMarkupEntry[]>,
  );
};

const markupEntryForm = groupInput({
  origin: input<RedoShopifyCountryCode>(),
  destination: input<RedoShopifyCountryCode>(),
  markup: input<number>(),
});

const markupsForm = groupInput({
  markups: listInput(
    () => markupEntryForm,
    () =>
      ({
        origin: RedoShopifyCountryCode.Us,
        destination: RedoShopifyCountryCode.Us,
        markup: 0,
      }) as LabelMarkupEntry,
    (entry) => ReturnBearMarkupKey.encode(entry.origin, entry.destination),
  ),
});

export const ReturnBearMarkupSettings = memo(
  function ReturnBearMarkupSettings() {
    const team = useRequiredContext(TeamContext);
    const client = useRequiredContext(RedoAdminRpcClientContext);

    const [integrationLoad, triggerIntegrationLoad] = useTriggerLoad(
      async () => {
        const result = await client.getReturnBearIntegration({
          teamId: team._id,
        });
        return result.integration;
      },
    );

    const initialMarkupsLoad = useLoad(async () => {
      if (!integrationLoad.value) return [];

      const integration = integrationLoad.value;
      const eligibleCountries = integration.eligibleShopperCountries;
      const destinations = await getReturnBearHubs(team, client);

      return generateRouteMarkups(
        eligibleCountries,
        destinations,
        integration.markups,
      );
    }, [integrationLoad.value, team, client]);

    useEffect(() => {
      triggerIntegrationLoad();
    }, [triggerIntegrationLoad]);

    if (integrationLoad.pending || initialMarkupsLoad.pending) {
      return (
        <Card title="Return Bear Markup">
          <Flex dir="column" gap="md">
            <Text>Loading...</Text>
          </Flex>
        </Card>
      );
    }

    if (!integrationLoad.value || !initialMarkupsLoad.value) {
      return null;
    }

    return (
      <ReturnBearMarkupSettingsInternal
        initialMarkups={initialMarkupsLoad.value}
        key={getKey(integrationLoad.value)}
        triggerReload={triggerIntegrationLoad}
      />
    );
  },
);

const ReturnBearMarkupSettingsInternal = memo(
  function ReturnBearMarkupSettingsInternal({
    initialMarkups,
    triggerReload,
  }: {
    initialMarkups: LabelMarkupEntry[];
    triggerReload: () => void;
  }) {
    const team = useRequiredContext(TeamContext);
    const client = useRequiredContext(RedoAdminRpcClientContext);

    const [savePending, setSavePending] = useState(false);

    const markupsInput = useInput(markupsForm, { markups: initialMarkups });

    const handleMarkupChange = useCallback(
      (
        origin: RedoShopifyCountryCode,
        destination: RedoShopifyCountryCode,
        value: string,
      ) => {
        const numValue = parseFloat(value) || 0;
        markupsInput.inputs.markups.setValue(
          markupsInput.inputs.markups.value.map((m) =>
            m.origin === origin && m.destination === destination
              ? { ...m, markup: numValue }
              : m,
          ),
        );
      },
      [markupsInput],
    );

    const handleSave = useCallback(async () => {
      setSavePending(true);
      try {
        await client.updateReturnBearMarkups({
          teamId: team._id,
          markups: markupsInput.inputs.markups.value,
        });

        toast("Label markups saved successfully", { variant: "success" });
        triggerReload();
      } catch (error) {
        toast("Failed to save label markups", { variant: "error" });
        console.error(error);
      } finally {
        setSavePending(false);
      }
    }, [client, team._id, markupsInput, triggerReload]);

    const markupsByOrigin = useMemo(
      () => groupMarkupsByOrigin(markupsInput.inputs.markups.value),
      [markupsInput.inputs.markups.value],
    );

    return (
      <Card title="Return Bear Markup">
        <Flex dir="column" gap="md">
          <Text>Please talk to Sam W before changing markups</Text>

          <Flex dir="column" gap="lg">
            {Object.entries(markupsByOrigin).map(([origin, routes]) => (
              <Flex dir="column" gap="sm" key={origin}>
                <Text fontWeight="medium">
                  From{" "}
                  {getCountryNameFromCode(origin as RedoShopifyCountryCode)}
                </Text>
                <Flex dir="column" gap="xs">
                  {routes.map((route) => (
                    <Flex
                      align="center"
                      gap="md"
                      key={`${route.origin}_${route.destination}`}
                    >
                      <Flex>
                        <Text>
                          to {getCountryNameFromCode(route.destination)}
                        </Text>
                      </Flex>
                      <Flex>
                        <RedoTextInput
                          inputTypeHint="number"
                          min={0}
                          placeholder="0.00"
                          setValue={(value) =>
                            handleMarkupChange(
                              route.origin,
                              route.destination,
                              value,
                            )
                          }
                          size="sm"
                          value={route.markup.toString()}
                        />
                      </Flex>
                      <Text>USD</Text>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>

          {markupsInput.changed && (
            <Flex justify="flex-start" pt="md">
              <RedoButton
                hierarchy="primary"
                onClick={handleSave}
                pending={savePending}
                size="lg"
                text="Save Markups"
                theme="normal"
              />
            </Flex>
          )}
        </Flex>
      </Card>
    );
  },
);
